import { Button as MantineButton, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconBriefcase,
  IconCheckbox,
  IconChevronDown,
  IconClockPause,
  IconEdit,
  IconRotateClockwise,
  IconTrash,
  IconUserCheck,
  IconX,
} from '@tabler/icons-react';
import { type Id, TaskActionType, type UpdateTasksStatusBody } from 'api/models';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import type { PropsWithChildren } from 'react';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import { TaskActionsAssign } from './TaskActionsAssign';
import { TaskActionsPostpone } from './TaskActionsPostpone';

type TaskActionsProps = {
  taskIds?: Id[];
  onClick: (data: UpdateTasksStatusBody) => void;
  onClickEdit?: () => void;
};

export const TaskActions = ({
  taskIds: task_ids,
  onClick,
  onClickEdit,
}: PropsWithChildren<TaskActionsProps>) => {
  const { t } = useTranslation();
  const profile = useProfile();

  const handlePostpone = () =>
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.TASK.ACTIONS.POSTPONE),
      children: (
        <TaskActionsPostpone
          onSubmit={({ days_postponed, ...data }) =>
            onClick({
              task_ids,
              action_type: TaskActionType.postpone,
              days_postponed: Number(days_postponed),
              ...data,
            })
          }
        />
      ),
    });

  const handleAssign = () =>
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.TASK.ACTIONS.ASSIGN),
      children: (
        <TaskActionsAssign
          onSubmit={data =>
            onClick({
              task_ids,
              action_type: TaskActionType.assign,
              ...data,
            })
          }
        />
      ),
    });

  const handleDelete = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.CONFIRM),
      centered: true,
      children: (
        <Text size="sm">{t(i18nKeys.AYS[`DELETE_TASK${task_ids?.length === 1 ? '' : 'S'}`])}</Text>
      ),
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        onClick({
          task_ids,
          action_type: TaskActionType.delete,
        }),
    });

  return (
    <Menu withArrow>
      <Menu.Target>
        <MantineButton variant="default" rightSection={<IconChevronDown size={20} />}>
          <Text size="sm">{t(i18nKeys.FORM.ACTIONS)}</Text>
        </MantineButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          color="green"
          leftSection={<IconCheckbox size={18} stroke={1.5} />}
          onClick={() =>
            onClick({
              task_ids,
              action_type: 'complete',
            })
          }
        >
          {t(i18nKeys.TASK.ACTIONS.COMPLETE)}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconX size={18} stroke={1.5} />}
          onClick={() =>
            onClick({
              task_ids,
              action_type: 'skip',
            })
          }
        >
          {t(i18nKeys.TASK.ACTIONS.SKIP)}
        </Menu.Item>
        <Menu.Item leftSection={<IconClockPause size={18} stroke={1.5} />} onClick={handlePostpone}>
          {t(i18nKeys.TASK.ACTIONS.POSTPONE)}
        </Menu.Item>
        <Menu.Item leftSection={<IconBriefcase size={18} stroke={1.5} />} onClick={handleAssign}>
          {t(i18nKeys.TASK.ACTIONS.ASSIGN)}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconUserCheck size={18} stroke={1.5} />}
          onClick={() =>
            onClick({
              task_ids,
              action_type: TaskActionType.assign_to_account_manager,
            })
          }
        >
          {t(i18nKeys.TASK.ACTIONS.ASSIGN_TO_ACCOUNT_MANAGER)}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconRotateClockwise size={18} stroke={1.5} />}
          onClick={() =>
            onClick({
              task_ids,
              action_type: 'revert',
            })
          }
        >
          {t(i18nKeys.TASK.ACTIONS.REVERT)}
        </Menu.Item>
        {onClickEdit && (
          <Menu.Item leftSection={<IconEdit size={18} stroke={1.5} />} onClick={onClickEdit}>
            {t(i18nKeys.EDIT)}
          </Menu.Item>
        )}
        {profile.isAdmin && (
          <Menu.Item
            color="red"
            leftSection={<IconTrash size={18} stroke={1.5} />}
            onClick={handleDelete}
          >
            {t(i18nKeys.TASK.ACTIONS.DELETE)}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
